import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useAuth } from "./auth";

// Phase 6.11.3 - Tenant identity resolution.
// Mirrors useUserOrganizations() in org.ts, but for the tenant user class.
// A "linked tenant" is a tenants row whose user_id = the current auth user.
// This is the client-side complement to the current_tenant_id() SQL helper;
// RLS enforces the real boundary, this hook just drives routing/UI.

export interface TenantIdentity {
  id: string;
  organization_id: string;
  first_name: string;
  last_name: string;
}

/**
 * Resolves the current auth user's linked tenant record, if any.
 * Returns null (not an error) when the user is not a tenant - e.g. a manager,
 * or a freshly-invited user who has not finished linking yet.
 */
export function useTenantIdentity() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["tenant-identity", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<TenantIdentity | null> => {
      const { data, error } = await supabase
        .from("tenants")
        .select("id, organization_id, first_name, last_name")
        .eq("user_id", user!.id)
        .is("archived_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data as TenantIdentity | null) ?? null;
    },
  });
}
